import { useEffect, useRef } from 'react';
import { Button } from '../../../components/Button';
import { AnchorPicker } from './AnchorPicker';
import type { TourAnchorsState } from './useTourAnchors';

export interface TourStepAdderProps {
  anchors: TourAnchorsState;
  /** True while the anchor list is open. */
  picking: boolean;
  onOpenPicker: () => void;
  onCancelPicker: () => void;
  /** Null is a centered step with nothing highlighted. */
  onSelectAnchor: (anchorId: string | null) => void;
  /**
   * The web page this tour runs on, for pick mode. Null on mobile and on a tour with no web route:
   * there is no page to stand on, so the anchor list is the only way in.
   */
  pickOnPageRoute: string | null;
  /** Pick mode's own save is running. */
  pickLoading: boolean;
  /**
   * Any write is running, pick mode's or another. Picking saves with the same draft token, so
   * starting it beside a save would 409 against the admin's own write.
   */
  pickDisabled: boolean;
  /** Called with `pickOnPageRoute`, so the editor never has to re-derive a route it was given. */
  onPickOnPage: (route: string) => void;
}

/**
 * The two ways to add a step: choose from the anchor list, or go and point at the element.
 *
 * Pick mode is web only. A mobile step is authored from the list, because there is no page in this
 * browser to point at.
 */
export const TourStepAdder = ({
  anchors,
  picking,
  onOpenPicker,
  onCancelPicker,
  onSelectAnchor,
  pickOnPageRoute,
  pickLoading,
  pickDisabled,
  onPickOnPage,
}: TourStepAdderProps) => {
  const addRef = useRef<HTMLButtonElement>(null);
  const wasPicking = useRef(false);

  // A cancelled picker unmounts the field that had focus, which would drop it to <body> and send
  // the next Tab back to the top of the page. Focus goes back to the button that opened it.
  // Selecting an anchor is not a cancel: the editor moves focus into the new step's form instead.
  useEffect(() => {
    if (picking) {
      wasPicking.current = true;
      return;
    }
    if (!wasPicking.current) return;
    wasPicking.current = false;
    if (document.activeElement === document.body) addRef.current?.focus();
  }, [picking]);

  if (picking) {
    return (
      <AnchorPicker
        anchors={anchors.anchors}
        status={anchors.status}
        heading="What should this step point at?"
        onRetry={anchors.retry}
        onSelect={onSelectAnchor}
        onCancel={onCancelPicker}
      />
    );
  }

  return (
    <div className="tour-editor__add">
      <Button
        ref={addRef}
        type="button"
        variant="secondary"
        onClick={onOpenPicker}
        data-testid="tour-add-step"
      >
        Add step
      </Button>
      {pickOnPageRoute && (
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onPickOnPage(pickOnPageRoute)}
            loading={pickLoading}
            disabled={pickDisabled}
            data-testid="tour-pick-on-page"
          >
            Pick element on page
          </Button>
          <span className="tour-editor__hint">
            Picking saves this draft first, then opens {pickOnPageRoute} so you can click the
            element.
          </span>
        </>
      )}
    </div>
  );
};
